import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
// tslint:disable-next-line:import-blacklist
import { UntypedFormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { debounceTime, finalize } from 'rxjs/operators';
import { ApBaseComponent, ApNotificationService, ApUndoableOperationService } from 'webcommon/core-ui';
import {
  ActionIconDto,
  ColumnActionIconsDto,
  ColumnCheckboxDto,
  ColumnDateDto,
  ColumnFieldType,
  ColumnHyperlinkDto,
  ColumnInfoDto,
  ColumnItemDto,
  ColumnStringDto,
  FindControlAdditionalSearchParametersDto,
  FindControlColumnConfigItemDto,
  // FindControlHiddenActionItemsDto,
  FindControlInitDto,
  FindControlRequiredSearchParametersDto,
  FindControlResultDto,
  FindControlSearchDto,
  HiddenActionItemType,
  IColumnPk,
  IDeleted,
  ISelected,
} from 'webcommon/shared';
import { ApFindControlService } from './ap-findcontrol.service';
import { FindControlMode } from '../models/index';

export enum ApFindControlAdvancedUpdateAction {
  Hide,
  Modify,
  New,
  Show,
}

export interface ApFindControlAdvancedUpdate {
  action: ApFindControlAdvancedUpdateAction;
  dataItem: IColumnPk & ISelected & IDeleted;
}

@Component({
  selector: 'ap-findcontrol-advanced',
  templateUrl: './ap-findcontrol-advanced.component.html',
  styleUrls: ['./ap-findcontrol-advanced.component.less'],
})
export class ApFindControlAdvancedComponent extends ApBaseComponent
  implements OnInit, OnChanges, OnDestroy {

  @Input() findControlId: string;
  @Input() tableBaseId: string;
  @Input() requiredSearchParameters?: FindControlRequiredSearchParametersDto;
  @Input() additionalSearchParameters?: FindControlAdditionalSearchParametersDto;
  @Input() mode: FindControlMode;
  @Input() updateObs: Observable<ApFindControlAdvancedUpdate> | undefined;
  @Input() undoComponentId: number;
  @Input() hiddenActionItems?: HiddenActionItemType[];

  @Output() deleteClick = new EventEmitter<IColumnPk & IDeleted>();
  @Output() finishLoad = new EventEmitter<FindControlInitDto>();
  @Output() finishLoadData = new EventEmitter<FindControlResultDto>();
  @Output() hyperlinkClick = new EventEmitter<IColumnPk & ISelected>();
  @Output() modifyClick = new EventEmitter<IColumnPk & ISelected>();
  @Output() newClick = new EventEmitter();
  @Output() selectChange = new EventEmitter<IColumnPk & ISelected>();

  clientColumnInfoList: Array<ColumnInfoDto<string>>;
  dataList: Array<IColumnPk & ISelected>;
  findControlDto: FindControlInitDto;
  formControl = new UntypedFormControl();
  inactiveCheckbox = new UntypedFormControl(false);
  isDeleteButtonHidden = false;
  isModifyButtonHidden = false;
  isNewButtonHidden = false;
  loadingConfig = true;
  loadingData = false;
  serverColumnItemList: Array<ColumnItemDto<string>>;


  private readonly waitTime: number = 300;

  constructor(
    public apNotificationService: ApNotificationService,
    private apFindControlService: ApFindControlService,
    private apUndoableOperationService: ApUndoableOperationService,
  ) {
    super();
  }

  ngOnInit() {
    if (this.hiddenActionItems) {
      this.isDeleteButtonHidden = this.hiddenActionItems.includes(HiddenActionItemType.Delete) ? true : false;
      this.isModifyButtonHidden = this.hiddenActionItems.includes(HiddenActionItemType.Modify) ? true : false;
      this.isNewButtonHidden = this.hiddenActionItems.includes(HiddenActionItemType.New) ? true : false;
    }

    this.load();

    this.formControl.valueChanges.pipe(
      debounceTime(this.waitTime),
      this.takeUntilDestroy(),
    ).subscribe((searchString) => {
      if (!searchString) return;
      this.loadData(searchString);
    });


    this.inactiveCheckbox.valueChanges.pipe(
      debounceTime(this.waitTime),
      this.takeUntilDestroy(),
    ).subscribe(() => {
      this.loadData(this.formControl.value);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.loadingConfig && changes.findControlId) {
      this.load();
    }
  }

  private load() {
    this.loadingConfig = true;
    this.apFindControlService.getConfig(this.findControlId).pipe(
      finalize(() => this.onLoadConfigComplete()),
      this.takeUntilDestroy(),
    ).subscribe(
      (data) => this.onLoadConfigSuccess(data),
      () => this.onError(),
    );

    if (this.updateObs) {
      this.updateObs.pipe(
        this.takeUntilDestroy(),
      ).subscribe((tableUpdate) => {
        switch (tableUpdate.action) {
          case ApFindControlAdvancedUpdateAction.Hide:
            this.hideItem(tableUpdate.dataItem);
            break;
          case ApFindControlAdvancedUpdateAction.Modify:
            this.updateItem(tableUpdate.dataItem);
            break;
          case ApFindControlAdvancedUpdateAction.New:
            this.appendItem(tableUpdate.dataItem);
            break;
          case ApFindControlAdvancedUpdateAction.Show:
            this.showItem(tableUpdate.dataItem);
            break;
        }
      });
    }
  }

  private appendItem(dataItem: IColumnPk) {
    const newDataList = [...this.dataList];
    newDataList.push(dataItem);
    this.dataList = newDataList;
  }

  private hideItem(dataItem: IDeleted) {
    dataItem.Deleted = true;
  }

  private showItem(dataItem: IDeleted) {
    dataItem.Deleted = false;
  }

  private updateItem(dataItem: IColumnPk) {
    const existingItemIndex = this.dataList.findIndex(x => x.Pk === dataItem.Pk);
    if (existingItemIndex !== -1) {
      const newDataList = [...this.dataList];
      newDataList.splice(existingItemIndex, 1, dataItem);
      this.dataList = newDataList;
    }
  }

  private loadData(searchStr: string, firstLoad = false) {
    const requiredSearchParams = this.requiredSearchParameters || {
      RequiredSearchParameters: [],
    };
    const searchDto: FindControlSearchDto = {
      AdditionalSearchParameters: this.additionalSearchParameters ?? null,
      Prepopulate: firstLoad,
      RequiredSearchParameters: requiredSearchParams,
      SearchText: searchStr,
      ShowInactive: this.inactiveCheckbox.value,
    };

    this.apUndoableOperationService.queueOperation(this.undoComponentId, () => {
      this.loadingData = true;
      const promise = this.apFindControlService.getData(this.findControlId, searchDto).toPromise().then(
          (data) => this.onLoadDataSuccess(data)
        ).finally(() => this.onLoadDataComplete());
      return {
        completionPromise: promise,
      };
    });
  }

  onSearchClick() {
    if (this.loadingData) return;
    this.formControl.setValue('', { emitEvent: false });
    this.loadData('%');
  }

  private onLoadConfigComplete() {
    this.loadingConfig = false;
  }

  private onLoadDataComplete() {
    this.loadingData = false;
  }

  private onLoadConfigSuccess(data: FindControlInitDto) {
    this.findControlDto = data;
    this.clientColumnInfoList = this.MapConfigToTableConfig(this.findControlDto);
    this.serverColumnItemList = this.MapConfigToVisibleConfig(this.findControlDto);

    this.appendAdditionalColumns();

    this.loadData('', true);

    this.finishLoad.emit(this.findControlDto);
  }

  private onLoadDataSuccess(resultDto: FindControlResultDto) {
    this.dataList = resultDto.Rows;
    this.finishLoadData.emit(resultDto);
  }

  private onError() {
    // TODO implement
  }

  private onDeleteClick(item: IColumnPk & IDeleted) {
    this.deleteClick.emit(item);
  }

  private onModifyClick(item: IColumnPk & ISelected) {
    this.modifyClick.emit(item);
  }

  onNewClick() {
    this.newClick.emit();
  }

  private onSelectChange(item: IColumnPk & ISelected) {
    this.selectChange.emit(item);
  }

  private italicize(x: IColumnPk): object {
    return x.Inactive ? {'font-style': 'italic'} : {};
  }

  private MapConfigToTableConfig(configDto: FindControlInitDto): Array<ColumnInfoDto<string>> {
    const tableColumnList: Array<ColumnInfoDto<string>> = configDto.FindControlBehavior.ColumnsConfig.Columns.map((column) => {
      const tableColumnDto = new ColumnInfoDto<string>();

      tableColumnDto.ColumnName = column.ColumnName;
      tableColumnDto.HeaderDisplayName = column.ColumnDisplayName;
      tableColumnDto.FieldTypeObject = column.CanSelect
        && this.mode !== FindControlMode.Administration
        ? this.createHyperlinkColumn(column)
        : this.createColumn(column);

      if (
        tableColumnDto.FieldTypeObject.FieldType !== ColumnFieldType.Dropdown &&
        tableColumnDto.FieldTypeObject.FieldType !== ColumnFieldType.Html &&
        tableColumnDto.FieldTypeObject.FieldType !== ColumnFieldType.Date
      ) {
        tableColumnDto.Format = { NgStyleProperties: this.italicize };
      }

      return tableColumnDto;
    });

    return tableColumnList;
  }

  private MapConfigToVisibleConfig(configDto: FindControlInitDto): Array<ColumnItemDto<string>> {
    const tableItemList: Array<ColumnItemDto<string>> = configDto.FindControlBehavior.ColumnsConfig.Columns
      .filter((column) => column.Visible)
      .map((column) => {
        return { ColumnName: column.ColumnName };
      });
    return tableItemList;
  }

  private createColumn(columnDto: FindControlColumnConfigItemDto<object>): ColumnStringDto | ColumnDateDto {
    if (columnDto.Type) {
      switch (columnDto.Type.toLowerCase()) {
        case ColumnFieldType.String.toLowerCase(): {
          return this.createStringColumn(columnDto);
        }
        case ColumnFieldType.Date.toLowerCase(): {
          return this.createDateColumn(columnDto);
        }
      }
    }
    return this.createStringColumn(columnDto);
  }

  private createStringColumn(columnDto: FindControlColumnConfigItemDto<object>): ColumnStringDto {
    const stringDto = new ColumnStringDto({
      FormatCallback: (dataDto: IColumnPk): string => dataDto[columnDto.ColumnName],
    });
    return stringDto;
  }

  private createDateColumn(columnDto: FindControlColumnConfigItemDto<object>): ColumnDateDto {
    const stringDto = new ColumnDateDto({
      DateFormat: 'M/d/y',
      DateStringCallback: (dataDto: IColumnPk): string => dataDto[columnDto.ColumnName],
    });
    return stringDto;
  }

  private createHyperlinkColumn(columnDto: FindControlColumnConfigItemDto<object>): ColumnHyperlinkDto {
    const hyperlinkDto: ColumnHyperlinkDto = new ColumnHyperlinkDto({
      HyperlinkCallback: (dataDto: IColumnPk & ISelected) => this.onHyperLinkSelect(dataDto),
      TextFieldTypeObject: this.createColumn(columnDto),
    });
    return hyperlinkDto;
  }

  private onHyperLinkSelect(dataDto: IColumnPk & ISelected) {
    this.hyperlinkClick.emit(dataDto);
  }

  private appendAdditionalColumns() {
    if (this.mode === FindControlMode.MultiSelect) {
      this.addCheckboxColumn();
    }
    this.addAdditionalActionsColumn();
  }

  private addCheckboxColumn() {
    const checkboxColumnName = 'Selected';

    this.clientColumnInfoList.unshift({
      ColumnName: checkboxColumnName,
      FieldTypeObject: new ColumnCheckboxDto({
        OnChangeCallback: (dataItem) => this.onSelectChange(dataItem),
      }),
      HeaderDisplayName: '',
    });
    this.serverColumnItemList.unshift({ColumnName: checkboxColumnName});
  }

  private addAdditionalActionsColumn() {
    const actionColumnName = 'Action';
    const actionIcons: ActionIconDto[] = [];
    if (this.findControlDto.SystemPermission.IsModifyAllowed && !this.isModifyButtonHidden) {
      actionIcons.push({
        MatIcon: 'edit',
        OnActionCallback: (dataItem) => this.onModifyClick(dataItem),
      });
    }
    if (this.findControlDto.SystemPermission.IsDeleteAllowed && !this.isDeleteButtonHidden) {
      actionIcons.push({
        MatIcon: 'delete',
        OnActionCallback: (dataItem) => this.onDeleteClick(dataItem),
      });
    }
    if (actionIcons.length) {
      this.clientColumnInfoList.push({
        ColumnName: actionColumnName,
        FieldTypeObject: new ColumnActionIconsDto({
          ActionIcons: actionIcons,
        }),
        HeaderDisplayName: actionColumnName,
      });
      this.serverColumnItemList.push({ColumnName: actionColumnName});
    }
  }
}
